import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { getContentfulUrl } from '@/utils/contentful'
import useMedia from '@/utils/hooks/useMedia'

export type SummaryPartParallaxProps = {
  appear?: boolean
  leaving?: boolean
  position?: 'left' | 'right'
  speed?: number
  alt?: string
  src?: string
}

export default function SummaryPartParallax({
  appear = false,
  leaving = false,
  position,
  speed,
  alt,
  src,
}: SummaryPartParallaxProps) {
  const { gsap } = useGSAP()
  const { t } = useTranslation()

  const parentRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const isReducedMotion = useMedia('(prefers-reduced-motion: reduce)')

  // const imgSrc = src ? getContentfulUrl(`/src/assets/images/summary/${src}.png`) : null;

  const betterFormat = useImageFormatContext()
  const [imgSrc, setImageUrl] = useState(null)
  useEffect(() => {
    if (src) {
      const url = getContentfulUrl(`/src/assets/images/summary/${src}.png`, {
        w: Math.round(((window.innerWidth * 1) / 3) * 2),
        fm: betterFormat,
      })
      setImageUrl(url)
    }
  }, [src, betterFormat])

  const itemClassName = (() => {
    switch (position) {
      case 'left':
        return 'left-[5%] -translate-x-1/2'
      case 'right':
        return 'right-0 flex justify-end'
      default:
        return ''
    }
  })()

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: parentRef.current,
          start: 'top-=256 bottom',
          end: 'bottom+=256 top',
          scrub: true,
        },
      })

      const rotationDirection = position === 'left' ? 1 : -1

      timeline.fromTo(
        imgRef.current,
        {
          yPercent: 100 * speed,
        },
        {
          yPercent: -100 * speed,
          ease: 'none',
        }
      )

      if (appear) {
        const appearTimeline = gsap.timeline({
          delay: 0.7,
        })
        appearTimeline.fromTo(
          containerRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.75,
            ease: 'power2.out',
          },
          0
        )
        appearTimeline.fromTo(
          containerRef.current,
          {
            yPercent: -30,
            rotate: -12 * rotationDirection,
          },
          {
            yPercent: -50,
            rotate: 0,
            duration: 0.75,
            ease: 'back.out(1.7)',
          },
          0
        )
      }
    })

    return () => ctx.revert()
  }, [appear, speed, position, parentRef, containerRef, imgRef, gsap])

  if (isReducedMotion) return null

  return (
    <section
      ref={parentRef}
      className={`relative hidden md:block w-full ${leaving ? 'transition-opacity duration-500 ease-out-quad opacity-0' : '0'}`}
    >
      <div ref={containerRef} className={`absolute z-1 top-0 translate-y-1/2 ${itemClassName}`}>
        <img ref={imgRef} className='aspect-square w-1/3 flex-none' src={imgSrc} alt={t(alt)} />
      </div>
    </section>
  )
}
